import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Home, Lock, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error('Missing reset token');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error('Password must be at least 6 characters');
    if (password !== confirm) return toast.error('Passwords do not match');
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setDone(true);
      toast.success('Password reset! Log in with your new password.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="glass-header bg-white/70 border-b border-border/40 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 flex items-center gap-3">
          <Home className="w-7 h-7 text-primary" />
          <span className="text-2xl font-bold tracking-tight text-primary">PrimePocket</span>
        </div>
      </header>
      <div className="flex-1 flex items-center justify-center p-6">
        <Card className="max-w-md w-full p-8" data-testid="reset-password-card">
          {done ? (
            <div className="text-center space-y-4">
              <div className="w-14 h-14 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-green-600" />
              </div>
              <h2 className="text-2xl font-semibold">Password reset</h2>
              <p className="text-muted-foreground">You can now sign in with your new password.</p>
              <Button data-testid="reset-goto-login-btn" onClick={() => navigate('/auth')} className="w-full rounded-full bg-primary hover:bg-primary/90">
                Go to Login
              </Button>
            </div>
          ) : !token ? (
            <div className="text-center space-y-4">
              <XCircle className="w-12 h-12 mx-auto text-destructive" />
              <h2 className="text-2xl font-semibold">Invalid link</h2>
              <p className="text-muted-foreground">This reset link is missing or malformed.</p>
              <Button data-testid="invalid-goto-login-btn" onClick={() => navigate('/auth')} variant="outline" className="w-full rounded-full">Go to Login</Button>
            </div>
          ) : (
            <>
              <div className="flex flex-col items-center text-center mb-6">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                  <Lock className="w-7 h-7 text-primary" />
                </div>
                <h2 className="text-2xl font-semibold">Choose a new password</h2>
                <p className="text-sm text-muted-foreground mt-2">At least 6 characters.</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>New password</Label>
                  <Input data-testid="reset-password-input" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} />
                </div>
                <div>
                  <Label>Confirm password</Label>
                  <Input data-testid="reset-confirm-input" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
                </div>
                <Button data-testid="reset-submit-btn" type="submit" disabled={submitting} className="w-full rounded-full bg-primary hover:bg-primary/90">
                  {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating...</> : 'Reset Password'}
                </Button>
              </form>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
